import {
  GetCPGCallBackLogsRequest,
  GetCPGCallBackLogsResponse,
} from "@services/models/Transactions/GetCPGCallBackLogsModel";
import { GetCPGRatesResponse } from "@services/models/Transactions/GetCPGRates";
import {
  GetPHPRateHistoryRequest,
  GetPHPRateHistoryResponse,
} from "@services/models/Transactions/GetPHPRateHistory";
import {
  GetCPGTransactionDetailsResponse,
  GetCPGTransactionListRequest,
  GetCPGTransactionListResponse,
} from "@services/models/Transactions/GetCPGTransactionListModel";
import { GetSupportedFiatCurrenciesResponse } from "@services/models/Transactions/GetSupportedBaseCurrencies";
import { GetSupportedCryptoCurrenciesResponse } from "@services/models/Transactions/GetSupportedCryptoCurrencies";
import { GetTokenListResponse } from "@services/models/Transactions/GetTokenList";
import { GetTransactionPayloadCounterpartyResponse } from "@services/models/Transactions/GetTransactionPayloadCounterparty";
import {
  GetTransactionReportRequest,
  GetTransactionReportResponse,
} from "@services/models/Transactions/GetTransactionReport";
import { SetExchangeRateResponse } from "@services/models/Transactions/SetExchangeRate";
import {
  SetPHPRateRequest,
  SetPHPRateResponse,
} from "@services/models/Transactions/SetPHPRate";
import {
  SettleTransactionRequest,
  SettleTransactionResponse,
} from "@services/models/Transactions/SettleTransactionModel";
import {
  ValidateTransactionDetialsRequest,
  ValidateTransactionDetialsResponse,
} from "@services/models/Transactions/ValidateTransactionDetails";
import { AxiosInstance } from "axios";
import { createApiClient } from "../api/createApiClient";

class TransactionApiService {
  private api: AxiosInstance;
  constructor() {
    this.api = createApiClient();
  }

  public async getCPGTransactionList(
    data: GetCPGTransactionListRequest,
  ): Promise<GetCPGTransactionListResponse> {
    try {
      const response = await this.api.get(
        "/analyticsDashboard/getCPGTransactionList",
        {
          params: {
            rows: data.rows,
            page: data.page,
            status: data.status,
            isPaying: data.isPaying,
            isPayout: data.isPayout,
            searchValue: data.searchValue?.trim() || undefined,
            compliancePending: data.compliancePending,
            search: true,
          },
        },
      );
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  public async getCPGTransactionDetails(
    data: GetCPGTransactionListRequest,
  ): Promise<GetCPGTransactionDetailsResponse> {
    try {
      const response = await this.api.get(
        "/analyticsDashboard/getCPGTransactionDetails",
        {
          params: {
            transactionId: data.transactionId,
          },
        },
      );
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  public async getTransactionPayloadCounterparty(
    merchantOrderId: string,
  ): Promise<GetTransactionPayloadCounterpartyResponse> {
    try {
      const response =
        await this.api.get<GetTransactionPayloadCounterpartyResponse>(
          "/analyticsDashboard/getTransactionPayloadCounterparty",
          {
            params: { merchantOrderId },
          },
        );
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  public async getTransactionReport(
    data: GetTransactionReportRequest,
  ): Promise<GetTransactionReportResponse> {
    try {
      const params: Record<string, any> = {};
      params.merchantId = data.merchantId;
      params.startDate = data.startDate;
      params.endDate = data.endDate;

      const url = "/analyticsDashboard/getTransactionReport";

      const response = await this.api.get<GetTransactionReportResponse>(url, {
        params,
      });

      return response.data;
    } catch (error) {
      throw error;
    }
  }

  public async settleTransaction(
    data: SettleTransactionRequest,
    endPoint: string = "/analyticsDashboard/settleTransaction",
  ): Promise<SettleTransactionResponse> {
    try {
      const response = await this.api.post(endPoint, data);
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  public async getTokenList(): Promise<GetTokenListResponse> {
    try {
      const response = await this.api.get<GetTokenListResponse>(
        "/missing-transactions/tokens",
      );
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  public async validateTransactionDetails(
    data: ValidateTransactionDetialsRequest,
  ): Promise<ValidateTransactionDetialsResponse> {
    try {
      const response = await this.api.get<ValidateTransactionDetialsResponse>(
        "/missing-transactions/search",
        {
          params: data,
        },
      );
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  public async getCPGCallbackLogs(
    data: GetCPGCallBackLogsRequest,
  ): Promise<GetCPGCallBackLogsResponse> {
    try {
      const response = await this.api.get(
        `/analyticsDashboard/getCPGCallbackLogs`,
        {
          params: {
            transactionId: data.transactionId,
          },
        },
      );
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  public async getSupportedCryptocurrencies(): Promise<GetSupportedCryptoCurrenciesResponse> {
    try {
      const response = await this.api.get<GetSupportedCryptoCurrenciesResponse>(
        "/invoices/getSupportedCryptoCurrenciesList",
      );
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  public async getSupportedFiatCurrencies(): Promise<GetSupportedFiatCurrenciesResponse> {
    try {
      const response = await this.api.get<GetSupportedFiatCurrenciesResponse>(
        "/invoices/getSupportedBaseCurrenciesList",
      );
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  public async setExchangeRate(
    data: unknown,
  ): Promise<SetExchangeRateResponse> {
    try {
      const response = await this.api.post<SetExchangeRateResponse>(
        "/analyticsDashboard/setRate",
        data,
      );
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  public async getCPGRates(merchantId: string): Promise<GetCPGRatesResponse> {
    try {
      const response = await this.api.get<GetCPGRatesResponse>(
        "/analyticsDashboard/getRates",
        {
          params: {
            merchantId: merchantId,
          },
        },
      );
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  public async setPHPRate(data: SetPHPRateRequest): Promise<SetPHPRateResponse> {
    try {
      const response = await this.api.post<SetPHPRateResponse>(
        "/analyticsDashboard/setPHPRate",
        data,
      );
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  public async getPHPRateHistory(
    params: GetPHPRateHistoryRequest,
  ): Promise<GetPHPRateHistoryResponse> {
    try {
      const response = await this.api.get<GetPHPRateHistoryResponse>(
        "/analyticsDashboard/getPHPRateHistory",
        {
          params: {
            currencySymbol: params.currencySymbol,
            limit: params.limit ?? 50,
            offset: params.offset ?? 0,
          },
        },
      );
      return response.data;
    } catch (error) {
      throw error;
    }
  }
}

export const transactionApiServices = new TransactionApiService();
